
import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnInit,
  Output
} from '@angular/core';

import {
  PageRequest,
  PaginationPage
} from 'src/app/common/modules/data-table.module/interfaces';

import { EAppointmentState } from 'src/app/entities/appointment.entity';

import {
  DoctorAppointment,
  EPatientAppointmentStatus
} from 'src/app/entities/appointment.entity/doctor-appointment.entity/doctor-appointment';

import { EDalyTimeSlotLocation } from
  'src/app/entities/daly-time-slot.entity/daly-time-slot';

import { Doctor } from 'src/app/entities/user.entity/doctor.entity';

import { DoctorSecretary } from
  'src/app/entities/user.entity/doctor.entity/secretary.entity';

import { AuthService } from
  'src/app/services/auth.service/auth.service';

import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-doctor-appointments-table',
  templateUrl: './doctor-appointments-table.component.html',
  styleUrls: ['./doctor-appointments-table.component.css']
})
export class DoctorAppointmentsTableComponent implements OnInit {

  @Input() currentPage: number = 0;
  @Input() perPage: number = 10;

  @Input() pageRequest!: (
    pageRequest: PageRequest
  ) => Promise<PaginationPage<DoctorAppointment>>;

  @Output() onEditRequest: EventEmitter<DoctorAppointment> =
    new EventEmitter();

  @Output() onPatientProfileRequest: EventEmitter<DoctorAppointment> =
    new EventEmitter();

  @Output() onAssignPrescriptionRequest: EventEmitter<DoctorAppointment> =
    new EventEmitter();

  @Output() onAssignConsultationRequest: EventEmitter<DoctorAppointment> =
    new EventEmitter();

  @Output() onAssignExplorationRequest: EventEmitter<DoctorAppointment> =
    new EventEmitter();

  @Output() onViewPrescriptionRequest: EventEmitter<DoctorAppointment> =
    new EventEmitter();

  @Output() onViewConsultationRequest: EventEmitter<DoctorAppointment> =
    new EventEmitter();

  @Output() onViewAttachmentsRequest: EventEmitter<DoctorAppointment> =
    new EventEmitter();

  @Output() onViewAttestationsRequest: EventEmitter<DoctorAppointment> =
    new EventEmitter();

  @Output() onViewTeleConsultationRequest: EventEmitter<DoctorAppointment> =
    new EventEmitter();

  @Output() onAccept: EventEmitter<DoctorAppointment> =
    new EventEmitter();

  @Output() onReject: EventEmitter<DoctorAppointment> =
    new EventEmitter();

  @Output() onAffect: EventEmitter<DoctorAppointment> =
    new EventEmitter();

  @Output() onPatientPositionChange: EventEmitter<{
    appointment: DoctorAppointment;
    position: EPatientAppointmentStatus;
  }> = new EventEmitter();

  @Output() onBillingRequest: EventEmitter<DoctorAppointment> =
    new EventEmitter();

  @Input() actions: Partial<{
    accept: boolean;
    reject: boolean;
    edit: boolean;
    delete: boolean;
    prescription: boolean;
    consultations: boolean;
    attachmentsView: boolean;
    payementsView: boolean;
    attestation: boolean;
    affect: boolean;
    exploration: boolean;
    patientPosition:boolean
  }> = {};

  @Input() emptyMessage?: string;

  isOpen: boolean = false;

  doctorAppointments: DoctorAppointment[] = [];

  hideCancelled: boolean = true;

  // =========================================
  // USER / DOCTOR
  // =========================================

  constructor(
    private authService: AuthService,
    private elementRef: ElementRef
  ) {}

  ngOnInit(): void {}

  get doctorConnect(): Doctor {
    return new Doctor(this.authService.getCurrentUser()!);
  }

  get user(): any {
    return this.authService.getCurrentUser() as any;
  }

  get Secterey(): any {
    return this.authService.getCurrentUser() as DoctorSecretary;
  }

  isDuplicateAllowed(): boolean {
    const doctor = this.user as Doctor;
    const secretary = this.user as DoctorSecretary;

    return doctor?.duplicateAppointment === true
      || secretary?.doctor?.duplicateAppointment === true;
  }

  // =========================================
  // APPOINTMENTS
  // =========================================

  avatarSrc(doctorAppointment: DoctorAppointment): string {
    return doctorAppointment.patientImageProfile
      ? `http${environment.API_SSL ? 's' : ''}://${environment.API_HOST_NAME}${environment.PORT ? ':' + environment.PORT : ''}/${environment.STORAGE_ENDPOINT}/${doctorAppointment.patientImageProfile}`
      : '/assets/img/user-avatar.png';
  }

  get filteredAppointments(): DoctorAppointment[] {
    return this.hideCancelled
      ? this.doctorAppointments.filter(a => a.status !== 2)
      : this.doctorAppointments;
  }

  toggleDropdown(): void {
    this.isOpen = !this.isOpen;

    const tableResponsive =
      this.elementRef.nativeElement.querySelector('.table-responsive');

    if (tableResponsive) {
      tableResponsive.classList.toggle('show-dropdown', this.isOpen);
    }
  }

  canOpenTeleconsultation(appointment: DoctorAppointment): boolean {
    return appointment.status === EAppointmentState.ACCEPTED
      && (
        appointment.location === EDalyTimeSlotLocation.BOTH
        || appointment.location === EDalyTimeSlotLocation.ONLINE
      );
  }

  canAssignPrescription(appointment: DoctorAppointment): boolean {
    return !appointment.hasPrescription;
  }

  getRemainingAmount(appointment: any): number {
    if (appointment.paidAmount >= 0) {
      return appointment.totalAmount - appointment.paidAmount;
    }

    return appointment.regularAmount - appointment.paidAmount;
  }

  get totalAppointmentsOnHold(): number {
    return this.doctorAppointments.length
      ? (this.doctorAppointments[0] as any).totalAppOnhold ?? 0
      : 0;
  }

  // =========================================
  // PATIENT POSITION
  // =========================================

  readonly patientPositions = [
    {
      value: EPatientAppointmentStatus.AT_HOME,
      label: 'Attendu',
      icon: '◷'
    },
    {
      value: EPatientAppointmentStatus.AT_CABINET,
      label: 'En attente',
      icon: '♧'
    },
    {
      value: EPatientAppointmentStatus.WITH_DOCTOR,
      label: 'En consultation',
      icon: '✚'
    },
    {
      value: EPatientAppointmentStatus.CABINET_OUT,
      label: 'Parti',
      icon: '✓'
    }
  ];

  openPresenceMenuId: string | null = null;

  getPositionIndex(
    status: EPatientAppointmentStatus | null | undefined
  ): number {
    const index = this.patientPositions.findIndex(
      position => position.value === status
    );

    return index >= 0 ? index : 0;
  }

  getPositionLabel(
    status: EPatientAppointmentStatus | null | undefined
  ): string {
    return this.patientPositions.find(
      position => position.value === status
    )?.label ?? 'Attendu';
  }

  getPositionIcon(
    status: EPatientAppointmentStatus | null | undefined
  ): string {
    return this.patientPositions.find(
      position => position.value === status
    )?.icon ?? '◷';
  }

  // Ouvrir / fermer le menu
togglePresenceMenu(appointmentId: string, event: MouseEvent): void {
    event.stopPropagation();

    this.openPresenceMenuId =
        this.openPresenceMenuId === appointmentId
            ? null
            : appointmentId;

    const table = this.elementRef.nativeElement.querySelector(
        '.show-dropdown-if-needed'
    );

    table?.classList.toggle(
        'show-dropdown',
        this.openPresenceMenuId !== null
    );
}

  // Passer à la position suivante
  nextPresence(appointment: DoctorAppointment): void {
    const currentIndex = this.getPositionIndex(
      appointment.patientAppointmentStatus
    );

    const nextIndex =
      (currentIndex + 1) % this.patientPositions.length;

    this.changePatientPosition(
      appointment,
      this.patientPositions[nextIndex].value
    );
  }

  // Émettre l'événement vers le parent
  changePatientPosition(
    appointment: DoctorAppointment,
    position: EPatientAppointmentStatus
  ): void {
    this.openPresenceMenuId = null;

    this.updateTableDropdownClass();

    if (appointment.patientAppointmentStatus === position) {
      return;
    }

    this.onPatientPositionChange.emit({
      appointment,
      position
    });
  }

  // Empêcher la fermeture lors d'un clic dans le menu
  stopPresenceMenuPropagation(event: MouseEvent): void {
    event.stopPropagation();
  }

  // Mise à jour du débordement du tableau
  private updateTableDropdownClass(): void {
    const tableResponsive =
      this.elementRef.nativeElement.querySelector('.show-dropdown-if-needed');

    if (tableResponsive) {
      tableResponsive.classList.toggle(
        'show-dropdown',
        this.openPresenceMenuId !== null
      );
    }
  }

  // Fermer le menu lors d'un clic extérieur
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const clickedInside =
      this.elementRef.nativeElement.contains(event.target);

    if (!clickedInside && this.openPresenceMenuId !== null) {
      this.openPresenceMenuId = null;
      this.updateTableDropdownClass();
    }
  }

  // Fermer avec la touche Escape
  @HostListener('document:keydown.escape')
  onEscapeKey(): void {
    if (this.openPresenceMenuId !== null) {
      this.openPresenceMenuId = null;
      this.updateTableDropdownClass();
    }
  }
}
